// Prayer times via the AlAdhan API (decisions.md TD-003), Umm al-Qura method (4).
// Given a masjid location + calendar date, returns the absolute UTC instant of each
// prayer. Jumu'ah uses the Dhuhr time (the khutbah is at Dhuhr on Friday).
//
// Called client-side by the masjid owner/manager to populate the server-side
// `prayer_times` cache via the `upsert_prayer_times` RPC (see
// supabase/migrations/20260713110000_phase4_prayer_times_sync.sql) — the same
// algorithm the Phase 2 reminder Edge Function uses server-side, kept in sync
// deliberately; if one changes, mirror the change in the other.

export type PrayerName = 'fajr' | 'dhuhr' | 'asr' | 'maghrib' | 'isha' | 'jumuah';

const ALADHAN_METHOD_UMM_AL_QURA = 4;

interface AlAdhanResponse {
  data: {
    timings: Record<string, string>;
    meta: { timezone: string };
  };
}

/** Converts a wall-clock time in an IANA timezone to an absolute UTC Date. */
function zonedToUtc(y: number, m: number, d: number, h: number, min: number, tz: string): Date {
  const guess = Date.UTC(y, m - 1, d, h, min);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(new Date(guess)).map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return new Date(guess - (asUtc - guess));
}

export async function fetchPrayerTimes(
  lat: number,
  lng: number,
  y: number,
  m: number,
  d: number,
): Promise<Record<PrayerName, Date>> {
  const dd = `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
  const url = `https://api.aladhan.com/v1/timings/${dd}?latitude=${lat}&longitude=${lng}&method=${ALADHAN_METHOD_UMM_AL_QURA}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`AlAdhan request failed: ${res.status}`);
  const json = (await res.json()) as AlAdhanResponse;
  const { timings, meta } = json.data;

  const at = (name: string): Date => {
    const raw = timings[name] ?? '00:00';
    const hhmm = raw.split(' ')[0] ?? '00:00';
    const [hStr, minStr] = hhmm.split(':');
    return zonedToUtc(y, m, d, Number(hStr ?? 0), Number(minStr ?? 0), meta.timezone);
  };

  const dhuhr = at('Dhuhr');
  return {
    fajr: at('Fajr'),
    dhuhr,
    asr: at('Asr'),
    maghrib: at('Maghrib'),
    isha: at('Isha'),
    jumuah: dhuhr,
  };
}
